// Generic visual mirroring for online versus.
//
// Abilities draw much of their look with their own game objects (Bastion's brace shimmer,
// Siege Field barrier, Fortress anchor, Wraith's echoes, Volt's overload aura, zone images…),
// not through ctx.fx, so the FX relay alone cannot show them to the opponent.
//
// VisualSender records every top-level game object created while the local mech / its
// abilities update (outside FX calls and projectile spawns, which are mirrored separately)
// and serialises their visual state with each state snapshot. VisualMirror rebuilds them on
// the other side: images / sprites (texture, frame, transform, tint, alpha, blend), graphics
// (their draw command buffer, re-sent only when it changes), rectangles, texts, and
// containers of those. Objects that stop appearing in the snapshots are removed.

import Phaser from 'phaser';

type GO = Phaser.GameObjects.GameObject;

/** One mirrored object (short keys keep the per-snapshot payload small). */
export interface VisNode {
  /** Id (sender side). */
  i: number;
  /** Kind: image, graphics, rectangle, text, container. */
  k: 'im' | 'gr' | 're' | 'tx' | 'co';
  x: number;
  y: number;
  sx: number;
  sy: number;
  r: number;
  a: number;
  d: number;
  v: 0 | 1;
  b?: number;
  /** Texture key / frame / tint (images). */
  t?: string;
  f?: string | number;
  c?: number;
  ox?: number;
  oy?: number;
  /** Graphics command buffer (only when it changed since the last send). */
  g?: number[];
  /** Rectangle size, fill, fill alpha, stroke colour / width / alpha. */
  w?: number;
  h?: number;
  fc?: number;
  fa?: number;
  sc?: number;
  sw?: number;
  sa?: number;
  /** Text content + colour + font size. */
  s?: string;
  tc?: string;
  fs?: string;
  /** Container children. */
  ch?: VisNode[];
}

const MAX_NODES = 48;
const r1 = (v: number): number => Math.round(v * 10) / 10;
const r3 = (v: number): number => Math.round(v * 1000) / 1000;

let nextId = 1;
const ids = new WeakMap<GO, number>();
const idOf = (o: GO): number => {
  let id = ids.get(o);
  if (!id) ids.set(o, (id = nextId++));
  return id;
};

export class VisualSender {
  private readonly tracked = new Set<GO>();
  private capturing = false;
  private suppress = 0;
  private readonly sentBuf = new Map<number, string>();
  private readonly onAdded = (o: GO): void => {
    if (this.capturing && this.suppress === 0 && o && !this.tracked.has(o)) {
      this.tracked.add(o);
      o.once(Phaser.GameObjects.Events.DESTROY, () => this.tracked.delete(o));
    }
  };

  constructor(private readonly scene: Phaser.Scene) {
    scene.events.on(Phaser.Scenes.Events.ADDED_TO_SCENE, this.onAdded);
  }

  /** Run `fn` with capture on (objects it creates are mirrored). */
  capture<T>(fn: () => T): T {
    const prev = this.capturing;
    this.capturing = true;
    try {
      return fn();
    } finally {
      this.capturing = prev;
    }
  }

  /** Run `fn` without capturing (FX calls, projectile spawns: mirrored by their own channels). */
  quiet<T>(fn: () => T): T {
    this.suppress++;
    try {
      return fn();
    } finally {
      this.suppress--;
    }
  }

  /** Never mirror these (the local mech's own view and its pieces). */
  exclude(o: GO): void {
    this.tracked.delete(o);
  }

  snapshot(): VisNode[] {
    const out: VisNode[] = [];
    for (const o of this.tracked) {
      if (out.length >= MAX_NODES) break;
      if (!o.active) continue;
      // Children are sent inside their container.
      if ((o as unknown as { parentContainer?: unknown }).parentContainer) continue;
      const n = this.node(o);
      if (n) out.push(n);
    }
    return out;
  }

  private node(o: GO): VisNode | null {
    const t = o as unknown as Phaser.GameObjects.Image;
    const base = {
      i: idOf(o),
      x: r1(t.x ?? 0),
      y: r1(t.y ?? 0),
      sx: r3(t.scaleX ?? 1),
      sy: r3(t.scaleY ?? 1),
      r: r3(t.rotation ?? 0),
      a: r3(t.alpha ?? 1),
      d: t.depth ?? 0,
      v: (t.visible === false ? 0 : 1) as 0 | 1,
    };
    const blend = typeof t.blendMode === 'number' && t.blendMode !== 0 ? (t.blendMode as number) : undefined;
    if (o instanceof Phaser.GameObjects.Container) {
      const ch = o.list.map((c) => this.node(c)).filter((c): c is VisNode => !!c);
      return { ...base, k: 'co', ch };
    }
    if (o instanceof Phaser.GameObjects.Graphics) {
      const buf = (o as unknown as { commandBuffer: number[] }).commandBuffer;
      const key = buf.length + ':' + buf.slice(0, 64).join(',') + ':' + buf.slice(-16).join(',');
      const n: VisNode = { ...base, k: 'gr', b: blend };
      if (this.sentBuf.get(base.i) !== key) {
        this.sentBuf.set(base.i, key);
        // Command buffers hold numbers only (colours, coordinates, command ids).
        n.g = buf.map((v) => (typeof v === 'number' ? Math.round(v * 100) / 100 : 0));
      }
      return n;
    }
    if (o instanceof Phaser.GameObjects.Rectangle) {
      return {
        ...base,
        k: 're',
        b: blend,
        w: o.width,
        h: o.height,
        fc: o.fillColor,
        fa: r3(o.fillAlpha),
        sc: o.strokeColor,
        sw: o.lineWidth,
        sa: o.isStroked ? r3(o.strokeAlpha) : 0,
        ox: r3(o.originX),
        oy: r3(o.originY),
      };
    }
    if (o instanceof Phaser.GameObjects.Text) {
      return { ...base, k: 'tx', s: o.text, tc: String(o.style.color), fs: String(o.style.fontSize), ox: r3(o.originX), oy: r3(o.originY) };
    }
    if (o instanceof Phaser.GameObjects.Image || o instanceof Phaser.GameObjects.Sprite) {
      return {
        ...base,
        k: 'im',
        b: blend,
        t: o.texture.key,
        f: o.frame.name,
        c: o.isTinted ? o.tintTopLeft : undefined,
        ox: r3(o.originX),
        oy: r3(o.originY),
      };
    }
    return null;
  }

  destroy(): void {
    this.scene.events.off(Phaser.Scenes.Events.ADDED_TO_SCENE, this.onAdded);
    this.tracked.clear();
  }
}

/** Rebuilds the opponent's ability visuals from their snapshots. */
export class VisualMirror {
  private readonly objs = new Map<number, GO>();
  private readonly bufs = new Map<number, number[]>();

  constructor(private readonly scene: Phaser.Scene) {}

  apply(nodes: VisNode[] | undefined): void {
    if (!Array.isArray(nodes)) return;
    const seen = new Set<number>();
    for (const n of nodes.slice(0, MAX_NODES)) {
      const o = this.sync(n, null, seen);
      void o;
    }
    for (const [id, o] of this.objs) {
      if (seen.has(id)) continue;
      o.destroy();
      this.objs.delete(id);
      this.bufs.delete(id);
    }
  }

  private sync(n: VisNode, parent: Phaser.GameObjects.Container | null, seen: Set<number>): GO | null {
    if (!n || typeof n.i !== 'number') return null;
    seen.add(n.i);
    let o = this.objs.get(n.i);
    if (o && !o.active) {
      this.objs.delete(n.i);
      o = undefined;
    }
    if (!o) {
      o = this.create(n) ?? undefined;
      if (!o) return null;
      this.objs.set(n.i, o);
      if (parent) parent.add(o);
    }
    const t = o as unknown as Phaser.GameObjects.Image;
    t.setPosition?.(n.x, n.y);
    t.setScale?.(n.sx, n.sy);
    t.setRotation?.(n.r);
    t.setAlpha?.(n.a);
    t.setVisible?.(n.v !== 0);
    if (!parent) t.setDepth?.(n.d);
    if (n.b !== undefined) t.setBlendMode?.(n.b);
    if (n.k === 'im' && o instanceof Phaser.GameObjects.Image) {
      if (n.t && (o.texture.key !== n.t || o.frame.name !== n.f) && this.scene.textures.exists(n.t)) o.setTexture(n.t, n.f);
      if (n.ox !== undefined) o.setOrigin(n.ox, n.oy);
      if (n.c !== undefined) o.setTint(n.c);
      else o.clearTint();
    } else if (n.k === 'gr' && o instanceof Phaser.GameObjects.Graphics) {
      if (Array.isArray(n.g)) this.bufs.set(n.i, n.g);
      const buf = this.bufs.get(n.i);
      if (buf) (o as unknown as { commandBuffer: number[] }).commandBuffer = buf.slice();
    } else if (n.k === 're' && o instanceof Phaser.GameObjects.Rectangle) {
      o.setSize(n.w ?? 1, n.h ?? 1).setOrigin(n.ox ?? 0.5, n.oy ?? 0.5).setFillStyle(n.fc ?? 0xffffff, n.fa ?? 1);
      if (n.sa) o.setStrokeStyle(n.sw ?? 1, n.sc ?? 0xffffff, n.sa);
    } else if (n.k === 'tx' && o instanceof Phaser.GameObjects.Text) {
      if (o.text !== n.s) o.setText(n.s ?? '');
      if (n.tc) o.setColor(n.tc);
      if (n.ox !== undefined) o.setOrigin(n.ox, n.oy);
    } else if (n.k === 'co' && o instanceof Phaser.GameObjects.Container) {
      for (const c of n.ch ?? []) this.sync(c, o, seen);
    }
    return o;
  }

  private create(n: VisNode): GO | null {
    const add = this.scene.add;
    switch (n.k) {
      case 'im':
        return n.t && this.scene.textures.exists(n.t) ? add.image(n.x, n.y, n.t, n.f) : null;
      case 'gr':
        return add.graphics();
      case 're':
        return add.rectangle(n.x, n.y, n.w ?? 1, n.h ?? 1);
      case 'tx':
        return add.text(n.x, n.y, n.s ?? '', { fontFamily: 'Chakra Petch, sans-serif', fontSize: n.fs ?? '16px', color: n.tc ?? '#ffffff' });
      case 'co':
        return add.container(n.x, n.y);
      default:
        return null;
    }
  }

  destroy(): void {
    for (const o of this.objs.values()) o.destroy();
    this.objs.clear();
    this.bufs.clear();
  }
}
